// src/features/players/usePlayerMutations.ts

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { createBatchMutationHook } from '@/shared/hooks/useBatchMutation';
import { PLAYER_QUERY_KEYS } from '@/lib/constants/player';
import { PlayerApi } from './PlayerApi';
import type { PlayerCreateRequest, PlayerUpdateRequest, PlayerViewData } from '@/lib/types/Player';

// --- Batch mutations via shared factory ---

export const useActivatePlayers = createBatchMutationHook(
  PlayerApi.activatePlayers,
  (cId) => PLAYER_QUERY_KEYS.list(cId),
);

export const useDeactivatePlayers = createBatchMutationHook(
  PlayerApi.deactivatePlayers,
  (cId) => PLAYER_QUERY_KEYS.list(cId),
);

export const useDeletePlayers = createBatchMutationHook(
  PlayerApi.deletePlayers,
  (cId) => PLAYER_QUERY_KEYS.list(cId),
);

// --- Create player ---

export function useCreatePlayer() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: PlayerCreateRequest) =>
      PlayerApi.createPlayer(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PLAYER_QUERY_KEYS.list(selectedCId!),
      });
    },
  });
}

// --- Copy player ---

export function useCopyPlayer() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ playerId, req }: { playerId: number; req: PlayerCreateRequest }) =>
      PlayerApi.copyPlayer(selectedCId!, playerId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PLAYER_QUERY_KEYS.list(selectedCId!),
      });
    },
  });
}

// --- Update player (full update) ---

export function useUpdatePlayer(playerId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: PlayerUpdateRequest) =>
      PlayerApi.updatePlayer(selectedCId!, playerId, req),
    onSuccess: (updated: PlayerViewData) => {
      // Write into detail cache
      queryClient.setQueryData(
        PLAYER_QUERY_KEYS.detail(selectedCId!, playerId),
        updated,
      );
      // Invalidate list to reflect changes (name, enabled, isDefault, etc.)
      queryClient.invalidateQueries({
        queryKey: PLAYER_QUERY_KEYS.list(selectedCId!),
      });
    },
  });
}
